import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import IcfLayout, { ScreenHeader } from "./IcfLayout";
import StoryNarratorBar from "./story/StoryNarratorBar";
import {
    Download,
    Share2,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    Search,
    Clock,
    Info,
} from "lucide-react";
import { P0118_EVIDENCE, PARTICIPANTS } from "../../data/icfSeed";

const RowLine = ({ k, v, ok, warn }) => (
    <div className="grid grid-cols-[180px_1fr] gap-4 py-2 border-b border-hair last:border-b-0">
        <div className="overline text-[9px] mt-1">{k}</div>
        <div className="text-[12.5px] text-neutral-800 leading-relaxed flex items-start gap-2">
            {ok && (
                <CheckCircle2
                    size={13}
                    className="text-[#16A34A] mt-[2px] shrink-0"
                />
            )}
            {warn && (
                <AlertTriangle
                    size={13}
                    className="text-[#CA8A04] mt-[2px] shrink-0"
                />
            )}
            <span>{v}</span>
        </div>
    </div>
);

const ChecklistRow = ({ item }) => {
    const isOk = item.status === "ok";
    const Icon = isOk ? CheckCircle2 : AlertTriangle;
    return (
        <div className="grid grid-cols-[120px_1fr_auto] gap-3 py-2 border-b border-hair last:border-b-0 items-center">
            <span className="mono text-[11px] text-[#C2410C]">§ {item.c}</span>
            <div className="text-[12.5px] text-neutral-800">
                {item.label}
                <span className="text-neutral-500 text-[11px] ml-2 mono">
                    · {item.where}
                </span>
            </div>
            <Icon
                size={13}
                className={isOk ? "text-[#16A34A]" : "text-[#CA8A04]"}
            />
        </div>
    );
};

const EvidenceBlock = ({ block, index }) => (
    <div className="border border-hair bg-white" data-testid={`evidence-${block.id}`}>
        <div className="px-5 py-3 border-b border-hair flex items-center gap-3">
            <div className="w-7 h-7 rounded-sm bg-[#0A0A0A] text-white flex items-center justify-center mono text-[11px]">
                {index + 1}
            </div>
            <div className="heading text-[14px] font-medium tracking-tight">
                {block.title}
            </div>
        </div>
        <div className="px-5 py-3">
            {block.rows && (
                <div>
                    {block.rows.map((r, i) => (
                        <RowLine key={i} {...r} />
                    ))}
                </div>
            )}
            {block.checklist && (
                <div>
                    {block.checklist.map((c, i) => (
                        <ChecklistRow key={i} item={c} />
                    ))}
                </div>
            )}
            {block.timeline && (
                <ol className="relative border-l border-hair ml-3 pl-6 mt-2">
                    {block.timeline.map((t, i) => (
                        <li key={i} className="mb-4 last:mb-0 relative">
                            <span
                                className={`absolute -left-[29px] top-1 w-3 h-3 rounded-sm border ${
                                    t.flag
                                        ? "bg-[#C2410C] border-[#C2410C]"
                                        : "bg-white border-neutral-400"
                                }`}
                            />
                            <div className="mono text-[11px] text-neutral-500">
                                {t.d}
                            </div>
                            <div
                                className={`text-[12.5px] ${
                                    t.flag ? "text-[#C2410C] font-medium" : "text-neutral-800"
                                }`}
                            >
                                {t.e}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    </div>
);

const Selector = ({ current, onChange }) => {
    const [q, setQ] = useState("");
    const options = PARTICIPANTS.slice(0, 15).map((p) => p.id);
    return (
        <aside
            className="border border-hair bg-white p-5"
            data-testid="explain-selector"
        >
            <div className="overline mb-4">Participant selector</div>
            <div className="relative">
                <Search
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Enter Participant ID (e.g. P-0118)"
                    data-testid="input-participant-search"
                    className="w-full border border-hair pl-9 pr-3 py-2 rounded-sm text-[13px] focus:outline-none focus:border-[#C2410C]"
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && q.trim()) {
                            onChange(q.trim());
                            setQ("");
                        }
                    }}
                />
            </div>
            <div className="overline mt-5 mb-2 text-[9px]">Or pick from list</div>
            <select
                value={current}
                onChange={(e) => onChange(e.target.value)}
                data-testid="dropdown-participant"
                className="w-full border border-hair bg-white px-3 py-2 rounded-sm text-[12.5px] focus:outline-none focus:border-[#C2410C]"
            >
                {[current, ...options.filter((o) => o !== current)].map((o) => (
                    <option key={o}>{o}</option>
                ))}
            </select>
            <div className="mt-6 border-t border-hair pt-4 text-[11px] text-neutral-500 leading-relaxed italic">
                <Info size={11} className="inline -mt-[2px] mr-1" />
                Randomly selected by BIMO inspector — P-0118 chosen for this walkthrough.
            </div>
            <div className="mt-6 border-t border-hair pt-4 text-[11px] mono uppercase tracking-wider text-neutral-500">
                Evidence pack ready
            </div>
            <p className="text-[11px] text-neutral-600 mt-2 leading-relaxed">
                Every element of the evidence chain is drawn from source
                systems (Florence eBinders, Veeva Vault eTMF, Advarra Central
                IRB) and cross-referenced to the applicable regulation.
            </p>
        </aside>
    );
};

export default function IcfExplain() {
    const [searchParams] = useSearchParams();
    const storyMode = searchParams.get("story") === "1";
    const [pid, setPid] = useState("P-0118");
    const [loading, setLoading] = useState(false);
    const [ev, setEv] = useState(P0118_EVIDENCE);

    const changePid = (newPid) => {
        setLoading(true);
        setTimeout(() => {
            // Only P-0118 has a real evidence chain. For anything else,
            // just re-render the same chain but under the new participant ID.
            setPid(newPid);
            setEv({ ...P0118_EVIDENCE, id: newPid });
            setLoading(false);
        }, 800);
    };

    useEffect(() => {
        setEv(P0118_EVIDENCE);
    }, []);

    return (
        <IcfLayout>
            <ScreenHeader
                title="Explain — Inspection-Ready Evidence"
                subtitle="For any participant, AudRI produces a complete, citation-traced evidence chain in seconds — the artifact a BIMO inspector, sponsor auditor, or IRB reviewer would ask for."
                right={
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            data-testid="btn-download-evidence"
                            className="inline-flex items-center gap-2 border border-hair bg-white text-sm px-3 py-2 rounded-sm hover:bg-neutral-50"
                        >
                            <Download size={13} /> Download Evidence Pack
                        </button>
                        <button
                            type="button"
                            data-testid="btn-share-auditor"
                            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white text-sm px-3 py-2 rounded-sm hover:bg-[#262626]"
                        >
                            <Share2 size={13} /> Share with Auditor
                        </button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-6">
                <Selector current={pid} onChange={changePid} />

                <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-5 flex-wrap">
                        <div className="overline">Evidence chain</div>
                        <span className="mono text-[11px] text-[#C2410C]">
                            {ev.id}
                        </span>
                        <span className="text-[11px] text-neutral-500 mono">
                            · {P0118_EVIDENCE.site} · {P0118_EVIDENCE.sponsor}
                        </span>
                    </div>

                    {loading ? (
                        <div className="border border-dashed border-hair bg-white p-16 text-center">
                            <Loader2
                                className="mx-auto text-neutral-400 animate-spin"
                                size={22}
                            />
                            <p className="text-[12.5px] text-neutral-500 mt-4">
                                Compiling evidence chain from Florence, Veeva
                                Vault, and Advarra IRB…
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {ev.blocks.map((b, i) => (
                                <EvidenceBlock
                                    key={b.id}
                                    block={b}
                                    index={i}
                                />
                            ))}
                        </div>
                    )}

                    <div className="mt-8 text-[12px] italic text-neutral-500 border-l-2 border-hair pl-4 leading-relaxed flex items-start gap-2">
                        <Clock size={12} className="mt-[3px] shrink-0" />
                        <span>
                            Generated in 1.4 seconds. In manual compilation,
                            this evidence chain typically takes 3–5 business
                            days across sponsor QA, site regulatory, and IRB
                            correspondence.
                        </span>
                    </div>
                </div>
            </div>

            {storyMode && <div className="h-32" />}
            {storyMode && (
                <StoryNarratorBar
                    slide={7}
                    label="3 · Explain"
                    body="Imagine your BIMO inspection tomorrow. The inspector picks one random participant — say P-0118. AudRI produces the seven-block evidence chain they'll ask for. Consent event with witness signature. IRB approval status at signing. Element-by-element 50.25 compliance. Recruitment consistency. Readability. Amendment re-consent status. Timeline. Every element cited to source. Generation time: 1.4 seconds."
                    ctaLabel="See the punchline"
                    nextTo="/icf/story/punchline"
                />
            )}
        </IcfLayout>
    );
}
